import React, { useState } from 'react';
import { ChevronDown, X, Menu, Phone } from 'lucide-react';
import LanguageSelector from './LanguageSelector';
import { useLanguage } from '../contexts/LanguageContext';

const Header = () => {
  const [showBanner, setShowBanner] = useState(true);
  const [showDropdown, setShowDropdown] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { t } = useLanguage();

  // Navigation links with translations
  const navLinks = [
    { label: t('nav.solutions'), href: '#', hasDropdown: true },
    { label: t('nav.docs'), href: '/docs' },
    { label: t('nav.pricing'), href: '/pricing' },
    { label: t('nav.contact'), href: '/contact-us' },
    { label: t('nav.bookAppointment'), href: '/book-appointment' }
  ];

  // Solutions dropdown with translations
  const solutionsDropdown = [
    { label: t('nav.docs'), href: '/docs' },
    { label: t('nav.pricing'), href: '/pricing' },
    { label: t('nav.contact'), href: '/contact-us' },
    { label: t('nav.bookAppointment'), href: '/book-appointment' },
    { label: t('nav.integrations'), href: '/integrations' },
    { label: t('nav.releases'), href: '/releases' }
  ];

  return (
    <>
      {/* Announcement Banner */}
      {showBanner && (
        <div className="bg-[#0d1117] border-b border-gray-800 py-2.5 px-4">
          <div className="max-w-7xl mx-auto flex items-center justify-center gap-3">
            <Phone className="w-4 h-4 text-teal-400" />
            <span className="text-gray-300 text-sm">
              {t('header.announcement')}
            </span>
            <a href="/releases" className="bg-teal-500 hover:bg-teal-600 text-white text-xs px-3 py-1 rounded-full transition-colors">
              {t('header.viewUpdate')} →
            </a>
            <button
              onClick={() => setShowBanner(false)}
              className="absolute right-4 text-gray-500 hover:text-gray-300 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Main Header */}
      <header className="bg-[#0a0a0b] py-4 px-6 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          {/* Logo */}
          <a href="/" className="flex items-center">
            <span className="text-2xl font-bold text-white tracking-tight">
              AI<span className="text-teal-400">CONNEQTORS</span>
            </span>
          </a>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8">
            {navLinks.map((link, index) => (
              <div key={index} className="relative">
                {link.hasDropdown ? (
                  <button
                    className="flex items-center gap-1 text-gray-300 hover:text-white transition-colors text-sm"
                    onMouseEnter={() => setShowDropdown(true)}
                    onMouseLeave={() => setShowDropdown(false)}
                  >
                    {link.label}
                    <ChevronDown className="w-4 h-4" />
                  </button>
                ) : (
                  <a
                    href={link.href}
                    className="text-gray-300 hover:text-white transition-colors text-sm"
                  >
                    {link.label}
                  </a>
                )}

                {/* Dropdown Menu */}
                {link.hasDropdown && showDropdown && (
                  <div
                    className="absolute top-full left-0 mt-2 w-56 bg-[#1a1a1f] rounded-lg shadow-xl border border-gray-800 py-2 z-50"
                    onMouseEnter={() => setShowDropdown(true)}
                    onMouseLeave={() => setShowDropdown(false)}
                  >
                    {solutionsDropdown.map((item, idx) => (
                      <a
                        key={idx}
                        href={item.href}
                        className="block px-4 py-2 text-gray-300 hover:text-white hover:bg-gray-800 transition-colors text-sm"
                      >
                        {item.label}
                      </a>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </nav>

          {/* Auth Buttons */}
          <div className="hidden lg:flex items-center gap-3">
            <LanguageSelector variant="compact" />
            <a 
              href="/signup" 
              className="text-gray-300 hover:text-white text-sm transition-colors"
            >
              {t('common.signUp')}
            </a>
            <a 
              href="/admin"
              className="border border-gray-600 text-gray-300 hover:border-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm font-medium transition-all duration-200"
              data-testid="admin-btn"
            >
              {t('header.admin')}
            </a>
            <a 
              href="/client"
              className="border border-teal-400 text-teal-400 hover:bg-teal-400 hover:text-black px-5 py-2 rounded-lg text-sm font-medium transition-all duration-200"
              data-testid="signin-btn"
            >
              {t('auth.signIn')}
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden text-white"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden mt-4 pb-4 border-t border-gray-800">
            <nav className="flex flex-col gap-4 pt-4">
              {navLinks.map((link, index) => (
                <a
                  key={index}
                  href={link.href}
                  className="text-gray-300 hover:text-white transition-colors px-2"
                >
                  {link.label}
                </a>
              ))}
              <a 
                href="/signup"
                className="text-gray-300 hover:text-white transition-colors px-2"
              >
                {t('common.signUp')}
              </a>
              <div className="flex gap-2 mx-2 mt-2">
                <a 
                  href="/admin"
                  className="flex-1 border border-gray-600 text-gray-300 hover:border-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm font-medium transition-all duration-200 text-center"
                >
                  {t('header.admin')}
                </a>
                <a 
                  href="/client"
                  className="flex-1 border border-teal-400 text-teal-400 hover:bg-teal-400 hover:text-black px-5 py-2 rounded-lg text-sm font-medium transition-all duration-200 text-center"
                >
                  {t('auth.signIn')}
                </a>
              </div>
            </nav>
          </div>
        )}
      </header>
    </>
  );
};

export default Header;
